import { type ReactNode, useMemo } from "react";
import classNames from "classnames";
import { Typography, type TypographyProps } from "@mui/material";
import { FixedWidthTileCarousel } from "~/common/components/carousel/FixedWidthTileCarousel";
import { useIsMinDesktopSizeL, useIsMinDesktopSizeM } from "~/common/hooks/useMediaQuery";
import { ProductRecommendationsTile, type trackingAttributesProps } from "./ProductRecommendationTile";
import { type ProductData } from "~/models/ProductData";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import classes from "./ProductRecommendationSection.module.less";
import { ProductRecommendationTileSkeleton } from "./ProductRecommendationTileSkeleton";

export enum ProductRecoType {
  topSeller = "top-seller",
  recentlyViewed = "recently-viewed",
  campaignSRP = "campaign-SRP",
  campaignPDP = "campaign-PDP",
  fccGap = "fcc",
}
interface Props {
  title: ReactNode;
  loading?: boolean;
  loadingLabel: string;
  className?: string;
  products: string[];
  variant: string;
  getTrackingAttributes: (
    product: ProductData,
    productDescriptor: ProductDescriptor,
    isPMA: boolean,
  ) => trackingAttributesProps;
}

export const ProductRecommendationSection = ({
  title,
  loading,
  loadingLabel,
  className,
  products,
  variant,
  ...rest
}: Props) => {
  const isDesktopL = useIsMinDesktopSizeL();
  const isDesktopM = useIsMinDesktopSizeM();

  const isSectionSmall = variant === ProductRecoType.topSeller || variant === ProductRecoType.campaignSRP;

  const numItems = useMemo(() => {
    let gap;
    let width;
    if (isSectionSmall) {
      gap = 16;
      width = 242;
    } else if (isDesktopL || isDesktopM) {
      gap = 32;
      width = 342;
    } else {
      gap = 16;
      width = 242;
    }
    return { gap, width };
  }, [window.innerWidth]);

  let items;
  let renderItem;
  let hideScrollButtons;
  if (loading) {
    items = ["", "", "", ""];
    renderItem = (productCode: string) => <ProductRecommendationTileSkeleton itemWidth={numItems.width} />;

    hideScrollButtons = true;
  } else {
    items = products;
    renderItem = (productCode: string) => (
      <ProductRecommendationsTile
        key={productCode}
        productCode={productCode}
        squeezePrice={true}
        itemWidth={numItems.width}
        variant={variant}
        {...rest}
      />
    );
    hideScrollButtons = false;
  }

  return (
    <div className={classNames(isSectionSmall ? classes.sectionRoot : classes.root, className)}>
      {title}

      <FixedWidthTileCarousel
        gap={numItems.gap}
        itemWidth={numItems.width}
        items={items}
        hideButtons={hideScrollButtons}
      >
        {renderItem}
      </FixedWidthTileCarousel>
    </div>
  );
};

export const ProductRecommendationTitle = (props: TypographyProps) => (
  <Typography {...props} className={classes.title} variant={"headlineSmall"} />
);
